import React, { useCallback, useEffect, useState } from 'react'
import { App, Radio, Space, Tag } from 'antd'
import { supabaseClient } from '../lib/supabaseClient'
import { brand } from '../theme/brand'
import { ListPage, SectionCard } from '../theme/listkit'
import { MessagesSquare, RefreshCw } from '../components/icons'

/**
 * Feed Layout — chooses which of the three feed presentations members see.
 *
 * The mobile app reads `app_config.feed_layout` from the single app_config row
 * (id = true) and subscribes to Realtime, so changing it here re-renders every
 * open app within seconds. No redeploy, no app-store release.
 *
 * Data access mirrors pages/KillSwitch.tsx (the other app_config control):
 *  - READ/WRITE via supabaseClient against `app_config` with .eq('id', true).
 *    The boolean primary key + singleton row don't fit Refine's BaseKey
 *    (string|number), so this bypasses the data provider deliberately.
 *  - RLS permits UPDATE only for admins; the touch_app_config() trigger stamps
 *    updated_at + updated_by, so every change is attributable in the audit trail.
 *
 * This is a presentation switch, not a kill switch: all three layouts render the
 * same posts with the same actions, so flipping it is safe on a live audience and
 * instantly reversible.
 */

const APP_CONFIG_RESOURCE = 'app_config'

export type FeedLayoutValue = 'original' | 'bet_a' | 'bet_b'

interface AppConfigFeedRow {
  feed_layout: FeedLayoutValue | null
  updated_at: string | null
}

interface LayoutOption {
  value: FeedLayoutValue
  label: string
  summary: string
  detail: string
}

const OPTIONS: LayoutOption[] = [
  {
    value: 'original',
    label: 'Original',
    summary: 'The layout that shipped.',
    detail:
      'Each item is a separate white card with a type label, a full Like · Comment · Share bar and a footer. The safe default — this is the presentation that has been through the full device test suite.',
  },
  {
    value: 'bet_a',
    label: 'Bet A · Roll Call',
    summary: 'One continuous column.',
    detail:
      'Drops the cards entirely: every row aligns to one left keyline, separated by hairlines and grouped under day headings ("Today", "This week"). Member posts keep full expression; joins, RSVPs and gifts collapse to a single line. Fits roughly three times as many items on screen.',
  },
  {
    value: 'bet_b',
    label: 'Bet B · Porch & Bulletin',
    summary: 'Members in the stream, the rest in a module.',
    detail:
      'The stream carries only members talking to each other, as full-width bands. News, joins, RSVPs and gifts move into one "Around the Academy" module that rolls repeats up ("6 alumni joined recently"). Deliberately shows fewer, weightier items.',
  },
]

/** "3 minutes ago" — compact relative time, degrading gracefully. */
function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'unknown'
  const sec = Math.round((Date.now() - then) / 1000)
  if (sec < 45) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.round(hr / 24)
  return `${day} day${day === 1 ? '' : 's'} ago`
}

export function FeedLayoutPage() {
  const { message } = App.useApp()

  const [layout, setLayout] = useState<FeedLayoutValue>('original')
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [saving, setSaving] = useState<FeedLayoutValue | null>(null)

  const loadConfig = useCallback(async () => {
    const { data, error } = await (
      supabaseClient.from(APP_CONFIG_RESOURCE) as unknown as {
        select: (c: string) => {
          eq: (
            col: string,
            v: boolean,
          ) => { maybeSingle: () => Promise<{ data: AppConfigFeedRow | null; error: unknown }> }
        }
      }
    )
      .select('feed_layout, updated_at')
      .eq('id', true)
      .maybeSingle()

    if (!error && data) {
      setLayout((data.feed_layout as FeedLayoutValue) ?? 'original')
      setUpdatedAt(data.updated_at ?? null)
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  async function applyLayout(next: FeedLayoutValue) {
    if (next === layout) return
    const previous = layout
    setSaving(next)
    // Optimistic: the control should feel immediate, but revert on failure so the
    // page never claims a layout that the database did not accept.
    setLayout(next)
    try {
      const { error } = await (
        supabaseClient.from(APP_CONFIG_RESOURCE) as unknown as {
          update: (v: Record<string, unknown>) => {
            eq: (col: string, v: boolean) => Promise<{ error: unknown }>
          }
        }
      )
        .update({ feed_layout: next })
        .eq('id', true)
      if (error) throw error

      const chosen = OPTIONS.find((o) => o.value === next)
      message.success(`Feed layout set to ${chosen?.label ?? next} — live for all members now.`)
      await loadConfig()
    } catch (e) {
      setLayout(previous)
      message.error(
        `Could not change the feed layout: ${e instanceof Error ? e.message : String(e)}`,
      )
    } finally {
      setSaving(null)
    }
  }

  const current = OPTIONS.find((o) => o.value === layout)

  return (
    <ListPage
      intro={{
        eyebrow: 'System · App Controls',
        description:
          'Choose which feed presentation members see. The change reaches every open app within seconds — no deploy and no app update — and is instantly reversible. All three layouts show the same posts with the same actions; only the shape changes.',
      }}
      stats={[
        {
          label: 'Live Layout',
          value: isLoading ? '…' : (current?.label ?? layout),
          icon: MessagesSquare,
          tone: layout === 'original' ? 'neutral' : 'gold',
        },
        {
          label: 'Last Changed',
          value: isLoading ? '…' : relativeTime(updatedAt),
          icon: RefreshCw,
          tone: 'neutral',
        },
      ]}
      statColumns={2}
    >
      <SectionCard title="Feed Layout">
        {isLoading ? (
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 500,
              fontSize: 13,
              color: brand.sub,
              padding: '24px 0',
            }}
          >
            Loading layout…
          </div>
        ) : (
          <Radio.Group
            data-testid="feed-layout-group"
            value={layout}
            disabled={saving !== null}
            onChange={(e) => void applyLayout(e.target.value as FeedLayoutValue)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {OPTIONS.map((opt) => {
                const isLive = opt.value === layout
                return (
                  <div
                    key={opt.value}
                    style={{
                      border: `1px solid ${isLive ? brand.gold : brand.line}`,
                      borderRadius: 10,
                      padding: '14px 16px',
                      background: isLive ? 'rgba(196,151,49,0.06)' : 'transparent',
                    }}
                  >
                    <Radio value={opt.value} data-testid={`feed-layout-${opt.value}`}>
                      <span
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 600,
                          fontSize: 14,
                          color: brand.ink,
                        }}
                      >
                        {opt.label}
                      </span>
                      <span
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 13,
                          color: brand.sub,
                          marginLeft: 8,
                        }}
                      >
                        {opt.summary}
                      </span>
                      {isLive ? (
                        <Tag color="gold" style={{ marginLeft: 10 }}>
                          Live
                        </Tag>
                      ) : null}
                    </Radio>
                    <div
                      style={{
                        fontFamily: brand.fSans,
                        fontWeight: 400,
                        fontSize: 12.5,
                        lineHeight: 1.6,
                        color: brand.sub,
                        marginTop: 8,
                        paddingLeft: 24,
                      }}
                    >
                      {opt.detail}
                    </div>
                  </div>
                )
              })}
            </Space>
          </Radio.Group>
        )}
      </SectionCard>
    </ListPage>
  )
}
